import type Decimal from 'decimal.js';
import { CheckCircle2, Clock, CreditCard, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney, getCurrencyDecimals } from '../../lib/money.js';
import { normalizeAmountTyping, safeDecimal } from '../../lib/numerals.js';

/** The customer page's «تسجيل دفعة» methods — one vocabulary for money taken in the office. */
const METHODS = ['cash', 'bank_transfer', 'fib', 'cheque', 'other'] as const;
type Choice = 'paid' | 'credit';

/**
 * «ترحيل» for a sales invoice: was it paid, or does it go on the customer's
 * account? (owner, 2026-11-01)
 *
 * An app order paid cash on delivery became an invoice, and posting it made a
 * debt — nothing asked whether the money had come in, so the shop cancelled
 * the invoices instead. Staff now have to pick one: «مدفوعة» posts AND records
 * the money against this invoice in the same request (the server checks they
 * may take money before it posts anything); «غير مدفوعة» posts it on account,
 * as before. Nothing is preselected — except an order already paid online
 * through FIB, which is the paid case by definition.
 */
export function PostInvoiceDialog({
  open,
  onOpenChange,
  invoice,
  outstanding,
  onPosted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: { id: string; docNumber: string; currencyCode: string; notes?: string | null; fibPaid?: boolean };
  /** What is still open on it — the whole total for a fresh draft. */
  outstanding: Decimal;
  onPosted: () => Promise<void> | void;
}): JSX.Element {
  const { t, i18n } = useTranslation('documents');
  const { t: tc } = useTranslation();
  const ccy = invoice.currencyCode;
  const dp = getCurrencyDecimals(ccy) ?? 2;
  const whole = outstanding.toDecimalPlaces(dp);
  /** A string, so the reset below runs on a real change — a Decimal is new every render. */
  const wholeText = whole.toFixed(dp);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [method, setMethod] = useState<string>('cash');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  // A fresh open: nothing chosen — unless FIB already took the money online.
  useEffect(() => {
    if (!open) return;
    setChoice(invoice.fibPaid ? 'paid' : null);
    setMethod(invoice.fibPaid ? 'fib' : 'cash');
    setAmount(wholeText);
    setBusy(false);
  }, [open, invoice.fibPaid, wholeText]);

  const money = (v: Decimal): string => formatMoney(v.toFixed(dp), { locale: i18n.language, currency: ccy });
  const typed = safeDecimal(amount);
  const amountOk = typed.gt(0) && typed.lte(whole);
  const rest = whole.minus(typed);
  const cod = /payment:\s*cod\b/i.test(invoice.notes ?? '');
  const canSubmit = !busy && choice !== null && (choice === 'credit' || amountOk);

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api().post<{ payment?: { paymentDocNumber: string | null; alreadyPaid: boolean } | null; paymentError?: string }>(
        `/invoices/${invoice.id}/post`,
        choice === 'paid' ? { paid: { method, amount: typed.toFixed(dp) } } : {},
      );
      if (choice === 'paid' && r.data.paymentError) {
        toast.warning(t('detail.postPay.payFailed', { defaultValue: "Posted, but the payment was not recorded — record it from the customer's page." }));
      } else if (choice === 'paid') {
        toast.success(t('detail.postPay.donePaid', {
          defaultValue: 'Posted — payment {{number}} recorded',
          number: r.data.payment?.paymentDocNumber ?? '',
        }));
      } else {
        toast.success(t('detail.postPay.doneCredit', { defaultValue: "Posted — on the customer's account" }));
      }
      onOpenChange(false);
      await onPosted();
    } catch (e) {
      const data = (e as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      toast.error(
        code === 'payment.not_allowed'
          ? t('detail.postPay.notAllowed', { defaultValue: "You can't take money — post it as not paid." })
          : code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'),
      );
    } finally {
      setBusy(false);
    }
  };

  const bigBtn = (c: Choice, icon: JSX.Element, label: string): JSX.Element => (
    <button
      type="button"
      onClick={() => setChoice(c)}
      aria-pressed={choice === c}
      className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-bold transition ${
        choice === c
          ? c === 'paid' ? 'border-success bg-success/10 text-fg' : 'border-warning bg-warning/10 text-fg'
          : 'border-border bg-bg text-fg-muted hover:bg-bg-subtle'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg gap-3 p-4">
        <DialogHeader>
          <DialogTitle>{t('detail.postPay.title', { defaultValue: 'Post invoice {{number}}', number: invoice.docNumber })}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-lg border border-border bg-bg-subtle/40 px-3 py-2 text-sm">
          <span className="text-fg-muted">{t('detail.postPay.total', { defaultValue: 'Invoice amount' })}</span>
          <span className="font-mono text-base font-bold tabular-nums" dir="ltr">{money(whole)}</span>
        </div>
        {invoice.fibPaid ? (
          <div className="rounded-md bg-success/10 px-3 py-1.5 text-xs font-semibold text-success">
            {t('detail.postPay.fib', { defaultValue: 'Paid online through FIB' })}
          </div>
        ) : cod ? (
          <div className="rounded-md bg-warning/10 px-3 py-1.5 text-xs font-semibold text-warning">
            {t('detail.postPay.cod', { defaultValue: 'App order — cash on delivery' })}
          </div>
        ) : null}

        <div className="text-sm font-semibold">{t('detail.postPay.question', { defaultValue: 'Has the customer paid this invoice?' })}</div>
        <div className="grid grid-cols-2 gap-2">
          {bigBtn('paid', <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />, t('detail.postPay.paid', { defaultValue: 'Paid — money received' }))}
          {bigBtn('credit', <Clock className="h-5 w-5 text-warning" aria-hidden />, t('detail.postPay.credit', { defaultValue: "Not paid — on the customer's account" }))}
        </div>

        {choice === 'paid' && (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-5 gap-1">
              {METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  aria-pressed={method === m}
                  className={`flex items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-xs font-semibold transition ${
                    method === m ? 'border-brand bg-brand-subtle text-fg' : 'border-border bg-bg text-fg-muted hover:bg-bg-subtle'
                  }`}
                >
                  {m === 'cash' ? <Wallet className="h-3.5 w-3.5" aria-hidden /> : <CreditCard className="h-3.5 w-3.5" aria-hidden />}
                  {tc(`parties:account.methods.${m}`, { defaultValue: m })}
                </button>
              ))}
            </div>
            <label className="flex flex-col gap-1 text-xs font-medium text-fg-muted">
              {t('detail.postPay.amount', { defaultValue: 'Amount received ({{ccy}})', ccy })}
              <input
                value={amount}
                onChange={(e) => setAmount(normalizeAmountTyping(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                inputMode="decimal"
                dir="ltr"
                className={`h-10 rounded-md border bg-bg px-3 text-end font-mono text-lg text-fg outline-none ${
                  amountOk || amount === '' ? 'border-border focus:border-brand' : 'border-danger'
                }`}
              />
            </label>
            {amountOk && rest.gt(0) && (
              <div className="text-xs text-warning">
                {t('detail.postPay.rest', { defaultValue: "{{amount}} stays on the customer's account", amount: money(rest) })}
              </div>
            )}
            {!amountOk && amount !== '' && (
              <div className="text-xs text-danger">
                {t('detail.postPay.tooMuch', { defaultValue: 'Between 0 and {{amount}}', amount: money(whole) })}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {tc('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={busy}>
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {t('detail.postPay.submit', { defaultValue: 'Post' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
